import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { timelineApi, TimelineCreateWithPhoto } from '@/api/timeline';
import type { TimelineEntryUpdate } from '@/types/timeline';
import { livestockDetailKey, livestockPhotoKeys } from './livestockPhotoKeys';
import { shareLinkKeys } from './shareLinkKeys';

const PAGE_SIZE = 50;

export function useTimeline(livestockId: number | undefined) {
  const { selectedTank } = useTank();
  const queryClient = useQueryClient();
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const queryKey = ['timeline', selectedTank?.id, livestockId];

  /**
   * An organism's compare index and single photos (a new, re-dated or deleted
   * photo changes them) and, with `detail`, its detail read (a deleted entry
   * can be its reference; plan 5.1).
   */
  const invalidatePhotos = (
    tankId: string | undefined,
    id: number | undefined,
    detail: boolean,
  ): void => {
    if (!tankId || !id) return;
    void queryClient.invalidateQueries({ queryKey: livestockPhotoKeys.all(tankId, id) });
    if (detail) void queryClient.invalidateQueries({ queryKey: livestockDetailKey(tankId, id) });
  };

  /** The tank's share links: their dates follow the photos (livestock compare plan 7.5). */
  const invalidateLinks = (tankId: string | undefined): void => {
    if (tankId) void queryClient.invalidateQueries({ queryKey: shareLinkKeys.tank(tankId) });
  };

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      timelineApi.list(selectedTank!.id, livestockId!, { limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((sum, p) => sum + p.entries.length, 0);
      return loadedCount < lastPage.total ? loadedCount : undefined;
    },
    enabled: !!selectedTank?.id && !!livestockId,
  });

  const createMutation = useMutation({
    mutationFn: (data: TimelineCreateWithPhoto) => {
      setUploadProgress(data.photo ? 0 : null);
      return timelineApi.create(
        selectedTank!.id,
        livestockId!,
        data,
        data.photo ? (percent) => setUploadProgress(percent) : undefined,
      );
    },
    onSuccess: (_result, variables) => {
      setUploadProgress(null);
      // Invalidate all timeline queries for this tank to avoid stale closure issues
      // (if user navigated to a different organism before the upload finished)
      queryClient.invalidateQueries({ queryKey: ['timeline', selectedTank?.id] });
      // Also invalidate livestock list so thumbnails update
      queryClient.invalidateQueries({ queryKey: ['livestock'] });
      // If targeting a different organism, invalidate its timeline too
      if (variables.target_livestock_id && variables.target_livestock_id !== livestockId) {
        queryClient.invalidateQueries({
          queryKey: ['timeline', selectedTank?.id, variables.target_livestock_id],
        });
      }
      invalidatePhotos(selectedTank?.id, livestockId, true);
      if (variables.target_livestock_id && variables.target_livestock_id !== livestockId) {
        invalidatePhotos(selectedTank?.id, variables.target_livestock_id, true);
      }
      notifications.show({ title: 'Success', message: 'Timeline entry added', color: 'green' });
    },
    onError: (error: Error) => {
      setUploadProgress(null);
      notifications.show({ title: 'Error', message: error.message || 'Failed to add entry', color: 'red' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ entryId, data }: { entryId: number; data: TimelineEntryUpdate }) =>
      timelineApi.update(selectedTank!.id, livestockId!, entryId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // A new date reorders the compare index, and changes the dates its links show
      invalidatePhotos(selectedTank?.id, livestockId, false);
      invalidateLinks(selectedTank?.id);
      notifications.show({ title: 'Success', message: 'Entry updated', color: 'green' });
    },
    onError: (error: Error) => {
      notifications.show({ title: 'Error', message: error.message || 'Failed to update', color: 'red' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (entryId: number) =>
      timelineApi.delete(selectedTank!.id, livestockId!, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      // Also invalidate livestock list so thumbnails update after photo deletion
      queryClient.invalidateQueries({ queryKey: ['livestock'] });
      // The photo leaves the compare index, and may have been the reference
      invalidatePhotos(selectedTank?.id, livestockId, true);
      // Links that showed it are broken now (their dates go null)
      invalidateLinks(selectedTank?.id);
      notifications.show({ title: 'Deleted', message: 'Entry removed', color: 'blue' });
    },
    onError: (error: Error) => {
      notifications.show({ title: 'Error', message: error.message || 'Failed to delete', color: 'red' });
    },
  });

  // Flatten all pages into a single entries array
  const allEntries = query.data?.pages.flatMap((page) => page.entries) ?? [];

  return {
    entries: allEntries,
    total: query.data?.pages[0]?.total ?? 0,
    isLoading: query.isLoading,
    hasMore: query.hasNextPage ?? false,
    loadMore: query.fetchNextPage,
    addEntry: createMutation.mutateAsync,
    updateEntry: updateMutation.mutateAsync,
    deleteEntry: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    uploadProgress,
  };
}
